import React, { useState, useEffect } from 'react';
import {
  withTranslation,
  Preloader,
  Root,
} from '@piwikpro/ui-components';
import { withInject, ConfigService, errorBoundary } from '@piwikpro/platform';
import * as PaddleJS from '@paddle/paddle-js';
import { useLocation } from '@tanstack/react-router';
import classNameBind from 'classnames/bind';
import styles from './Checkout.module.css';

const bindClasses = classNameBind.bind(styles);

// Paddle checkout events to be tracked in ChangePlanOverlay on return redirect
const TRACKED_PADDLE_EVENTS = [
  'checkout.closed',
  'checkout.updated',
  'checkout.completed',
  'checkout.payment.failed',
  'checkout.discount.applied',
  'checkout.warning',
  'checkout.error',
] as const;

type TrackedPaddleEvent = typeof TRACKED_PADDLE_EVENTS[number];

const getCheckoutLang = (userLang: string) => {
  const PADDLE_SUPPORTED_LANGS = ['en', 'de', 'fr', 'sv', 'es', 'da'];

  const langPart = userLang.split('-')[0];
  if (langPart === 'ca') {
    return 'es';
  }

  if (PADDLE_SUPPORTED_LANGS.includes(langPart)) {
    return langPart;
  }

  return 'en';
};

interface InjectedProps {
  config: ConfigService
}

interface Props extends InjectedProps {
}

const Checkout: React.FunctionComponent<Props> = ({
  config,
}) => {
  const location = useLocation();
  const [missingParams, setMissingParams] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const paddleEnvironment = config.get('PADDLE_ENVIRONMENT');
  const paddleToken = config.get('PADDLE_UI_TOKEN');
  const searchParams = new URLSearchParams(
    typeof location.search === 'string'
      ? location.search
      : (location as { searchStr?: string }).searchStr || '',
  );

  const queryParams = {
    priceId: {
      value: searchParams.get('price_id'), required: true, name: 'price_id',
    },
    organizationId: {
      value: searchParams.get('organization_id'), required: true, name: 'organization_id',
    },
    organizationName: {
      value: searchParams.get('organization_name'), required: true, name: 'organization_name',
    },
    userId: {
      value: searchParams.get('user_id'), required: true, name: 'user_id',
    },
    dataCenter: {
      value: searchParams.get('data_center'), required: true, name: 'data_center',
    },
    redirectOrigin: {
      value: searchParams.get('redirect_origin'), required: false, name: 'redirect_origin',
    },
    userLanguage: {
      value: searchParams.get('language') || 'en', required: false, name: 'language',
    },
  };

  const handlePaddleEvents = async (event: PaddleJS.PaddleEventData) => {
    if (TRACKED_PADDLE_EVENTS.includes(event.name as TrackedPaddleEvent)) {
      window.parent.postMessage({ type: event.name, source: 'paddle-checkout' }, '*');
    }

    if (event.name === 'checkout.completed' && window.parent === window) {
      const paymentBaseUrl = config.get('PAYMENT_BASE_URL') || '/payment';
      window.location.assign(`${paymentBaseUrl}/success`);
    }
  };

  const openPaddleCheckout = (paddle: PaddleJS.Paddle) => {
    const missing = Object.values(queryParams).filter(param => !param.value && param.required).map(param => param.name);
    setMissingParams(missing);

    if (missing.length > 0) {
      return;
    }

    paddle?.Checkout.open({
      items: [{ priceId: queryParams.priceId.value as string, quantity: 1 }],
      customData: {
        organization_id: queryParams.organizationId.value as string,
        data_center: (queryParams.dataCenter.value as string).split('.')[0],
        user_id: queryParams.userId.value as string,
      },
    });
  };

  const onPaddleInit = (paddleInstance: PaddleJS.Paddle | undefined) => {
    if (paddleInstance) {
      openPaddleCheckout(paddleInstance);
    }
  };

  const initializePaddleCheckout = async () => {
    try {
      const paddleInstance = await PaddleJS.initializePaddle({
        environment: paddleEnvironment as PaddleJS.Environments,
        token: paddleToken,
        checkout: {
          settings: {
            locale: getCheckoutLang(queryParams.userLanguage.value),
          },
        },
        eventCallback: handlePaddleEvents,
      });

      onPaddleInit(paddleInstance);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializePaddleCheckout();
  }, []);

  if (isLoading) {
    return <Preloader />;
  }

  if (missingParams.length > 0) {
    return (
      <Root withThemes>
        <div className={bindClasses('errorContainer')}>
          <div className={bindClasses('errorIcon')}>
            <svg width="111" height="99" viewBox="0 0 111 99" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M63.3837 4.62847L109.48 84.4695C113.043 90.6407 108.589 98.3548 101.463 98.3548H9.27073C2.14474 98.3548 -2.30899 90.6407 1.254 84.4695L47.3502 4.62846C50.9132 -1.54283 59.8207 -1.54281 63.3837 4.62847ZM59.3753 6.9427C57.5938 3.85706 53.1401 3.85705 51.3586 6.94269L5.26237 86.7837C3.48087 89.8693 5.70774 93.7264 9.27073 93.7264H101.463C105.026 93.7264 107.253 89.8693 105.472 86.7837L59.3753 6.9427Z" />
              <rect x="50.1562" y="30.6636" width="10.4138" height="33.5557" rx="5.20692" />
              <circle cx="55.3632" cy="78.1054" r="5.20692" />
            </svg>
          </div>
          <h1 className={bindClasses('errorTitle')}>
            Error
          </h1>
          <p className={bindClasses('errorMessage')}>
            {`Missing ${missingParams.join(', ')} query param`}
          </p>
        </div>
      </Root>
    );
  }

  return (
    <div className={bindClasses('checkoutContainer')} />
  );
};

export default withInject<InjectedProps>({
  config: 'config',
})(
  withTranslation('administration')(errorBoundary(Checkout)),
);
